import { Injectable, inject, signal } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { Company, CompanyService } from '../../services/company.service';

/**
 * The company being viewed, shared by the company page and its tabs. Provided by the `Company`
 * page component, so every routed tab under it gets the same instance.
 */
@Injectable()
export class CompanyContext {
  private readonly companyService = inject(CompanyService);

  readonly company = signal<Company | null>(null);
  readonly loadError = signal(false);

  load(id: string): Observable<Company> {
    return this.companyService.get(id).pipe(
      tap({
        next: (company) => {
          this.company.set(company);
          this.loadError.set(false);
        },
        error: () => this.loadError.set(true),
      })
    );
  }

  /** Tabs that change the company push the server's answer here. */
  set(company: Company): void {
    this.company.set(company);
  }
}

/**
 * The company's user, for the reused user tabs (see `accountUserParams`). Null until a user is
 * invited; the page then shows an empty state instead of those tabs.
 */
export const companyUserIdResolver: ResolveFn<string | null> = (route) =>
  inject(CompanyService)
    .get(route.paramMap.get('id') || '')
    .pipe(
      map((company) => company?.primaryUserId ?? null),
      catchError(() => of(null))
    );
